import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Collapse,
  Box,
  Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Minimize as MinimizeIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from "@material-ui/icons";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  progressContainer: {
    position: "fixed",
    bottom: 20,
    right: 20,
    zIndex: 9999,
    minWidth: 300,
    maxWidth: 400,
    boxShadow: theme.shadows[10],
    transition: "all 0.3s ease",
  },
  minimized: {
    minWidth: 200,
    maxWidth: 250,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    cursor: "pointer",
  },
  content: {
    padding: theme.spacing(2),
  },
  progressBar: {
    marginTop: theme.spacing(1),
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    marginTop: theme.spacing(1),
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  stats: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  minimizedContent: {
    padding: theme.spacing(1),
    textAlign: "center",
  },
}));

const DiskCleanupProgress = ({ companyId, onComplete }) => {
  const classes = useStyles();
  const [minimized, setMinimized] = useState(false);
  const [status, setStatus] = useState("running");
  const [progress, setProgress] = useState(0);
  const [filesDeleted, setFilesDeleted] = useState(0);
  const [spaceFreed, setSpaceFreed] = useState(0);
  const [currentFolder, setCurrentFolder] = useState("");
  const [error, setError] = useState(null);
  const { socket } = useContext(AuthContext);
  const safetyTimeoutRef = useRef(null);

  useEffect(() => {
    let pollingInterval = null;
    let socketListener = null;
    let completionTimeout = null;

    // Buscar status inicial
    const fetchStatus = async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/disk-cleanup/status`);
        if (data.status !== "idle") {
          setStatus(data.status);
          setProgress(data.progress || 0);
          if (data.data) {
            setFilesDeleted(data.data.filesDeleted || 0);
            setSpaceFreed(data.data.spaceFreed || 0);
          }
          
          // Se completou ou teve erro, chamar onComplete e parar polling
          if (data.status === "completed" || data.status === "error") {
            if (pollingInterval) {
              clearInterval(pollingInterval);
              pollingInterval = null;
            }
            // Limpar timeouts se existirem
            if (completionTimeout) {
              clearTimeout(completionTimeout);
            }
            if (safetyTimeoutRef.current) {
              clearTimeout(safetyTimeoutRef.current);
              safetyTimeoutRef.current = null;
            }
            // Chamar onComplete imediatamente quando detectar conclusão via polling
            if (onComplete) {
              console.log("[DiskCleanup] Chamando onComplete via polling com status:", data.status);
              onComplete({
                status: data.status,
                filesDeleted: data.data?.filesDeleted || 0,
                spaceFreed: data.data?.spaceFreed || 0,
                error: data.error
              });
            }
          } else if (data.status === "running" || data.status === "active") {
            // Se ainda está rodando, iniciar polling como fallback
            if (!pollingInterval) {
              pollingInterval = setInterval(fetchStatus, 2000);
            }
          }
        }
      } catch (error) {
        console.error("Erro ao buscar status da limpeza:", error);
      }
    };

    fetchStatus();

    // Escutar eventos de progresso via socket
    const eventName = `diskCleanupProgress`;
    if (socket) {
      socketListener = (data) => {
        console.log("[DiskCleanup] Evento recebido via socket:", data);
        if (data.companyId === companyId) {
          setStatus(data.status);
          setProgress(data.progress || 0);
          setFilesDeleted(data.filesDeleted || 0);
          setSpaceFreed(data.spaceFreed || 0);
          setCurrentFolder(data.currentFolder || "");
          setError(data.error || null);

          // Parar polling se recebeu evento via socket
          if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
          }

          if (data.status === "completed" || data.status === "error") {
            // Parar polling quando completar
            if (pollingInterval) {
              clearInterval(pollingInterval);
              pollingInterval = null;
            }
            // Limpar timeouts se existirem
            if (completionTimeout) {
              clearTimeout(completionTimeout);
            }
            if (safetyTimeoutRef.current) {
              clearTimeout(safetyTimeoutRef.current);
              safetyTimeoutRef.current = null;
            }
            // Chamar onComplete imediatamente
            if (onComplete) {
              console.log("[DiskCleanup] Chamando onComplete com status:", data.status);
              onComplete(data);
            }
          } else if (data.status === "running") {
            // Reiniciar polling se voltar a rodar
            if (!pollingInterval) {
              pollingInterval = setInterval(fetchStatus, 2000);
            }
          }
        }
      };
      
      socket.on(eventName, socketListener);
      console.log("[DiskCleanup] Listener de socket registrado para:", eventName);
    } else {
      console.warn("[DiskCleanup] Socket não disponível, usando apenas polling");
      // Se não tem socket, usar polling mais frequente
      pollingInterval = setInterval(fetchStatus, 2000);
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (completionTimeout) {
        clearTimeout(completionTimeout);
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
      if (socket && socketListener) {
        socket.off(eventName, socketListener);
      }
    };
  }, [companyId, socket, onComplete]);

  // Timeout de segurança: se o status for "completed" mas o onComplete não foi chamado após 3 segundos, chamar
  useEffect(() => {
    if (status === "completed" || status === "error") {
      // Limpar timeout anterior se existir
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
      
      // Criar novo timeout de segurança
      safetyTimeoutRef.current = setTimeout(() => {
        console.log("[DiskCleanup] Timeout de segurança: chamando onComplete após 3 segundos");
        if (onComplete) {
          onComplete({
            status,
            filesDeleted,
            spaceFreed,
            error
          });
        }
        safetyTimeoutRef.current = null;
      }, 3000);
      
      return () => {
        if (safetyTimeoutRef.current) {
          clearTimeout(safetyTimeoutRef.current);
          safetyTimeoutRef.current = null;
        }
      };
    } else {
      // Se o status não for completed/error, limpar timeout
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    }
  }, [status, filesDeleted, spaceFreed, error, onComplete]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const handleClose = () => {
    if (status === "completed" || status === "error") {
      // Se completou ou teve erro, pode fechar
      if (onComplete) {
        onComplete({ status, filesDeleted, spaceFreed });
      }
    }
  };

  // Se o status for completed ou error, o componente ainda deve ser renderizado
  // para mostrar o resultado final antes de fechar
  // O fechamento é controlado pelo onComplete no ModernLayout
  if (status === "idle") {
    return null;
  }

  return (
    <Paper className={`${classes.progressContainer} ${minimized ? classes.minimized : ""}`}>
      <Box
        className={classes.header}
        onClick={() => setMinimized(!minimized)}
      >
        <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
          Limpeza de Disco
        </Typography>
        <Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMinimized(!minimized);
            }}
            style={{ color: "inherit" }}
          >
            <MinimizeIcon fontSize="small" />
          </IconButton>
          {(status === "completed" || status === "error") && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              style={{ color: "inherit" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={!minimized}>
        <Box className={classes.content}>
          {status === "running" && (
            <>
              <Typography variant="body2">
                Processando... {progress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                className={classes.progressBar}
              />
              {currentFolder && (
                <Typography variant="caption" className={classes.statusText}>
                  Pasta: {currentFolder}
                </Typography>
              )}
            </>
          )}

          {status === "completed" && (
            <>
              <Box style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="body2" style={{ fontWeight: "bold" }}>
                  Limpeza Concluída!
                </Typography>
              </Box>
              <Box className={classes.stats}>
                <Chip
                  label={`${filesDeleted} arquivos deletados`}
                  size="small"
                  color="primary"
                />
                <Chip
                  label={`${formatBytes(spaceFreed)} liberados`}
                  size="small"
                  color="secondary"
                />
              </Box>
            </>
          )}

          {status === "error" && (
            <>
              <Box style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <ErrorIcon color="error" />
                <Typography variant="body2" style={{ fontWeight: "bold", color: "error" }}>
                  Erro na Limpeza
                </Typography>
              </Box>
              {error && (
                <Typography variant="caption" color="error">
                  {error}
                </Typography>
              )}
            </>
          )}

          {status === "running" && (
            <Box className={classes.stats}>
              <Chip
                label={`${filesDeleted} deletados`}
                size="small"
              />
              <Chip
                label={`${formatBytes(spaceFreed)} liberados`}
                size="small"
              />
            </Box>
          )}
        </Box>
      </Collapse>

      {minimized && (
        <Box className={classes.minimizedContent}>
          <LinearProgress
            variant="determinate"
            value={progress}
            className={classes.progressBar}
          />
          <Typography variant="caption" style={{ fontSize: "0.75rem" }}>
            {progress}%
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DiskCleanupProgress;

